import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { get } from 'lodash';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { Grid, Box, ButtonBase, Typography, CardContent, Avatar, Divider, styled } from '@mui/material';
import {
  Add as AddIcon,
  StoreMallDirectory as StoreMallDirectoryIcon,
  EmojiPeople as EmojiPeopleIcon,
  Portrait as PortraitIcon, // id mode
  QueryBuilder as QueryBuilderIcon, // sd - to do
  School as SchoolIcon, // learn
  AddShoppingCart as AddShoppingCartIcon, // claim rescue
  CalendarToday as CalendarTodayIcon, // receiver - schedule
  PersonAdd as PersonAddIcon, // receiver - add staff
  AccessTime as AccessTimeIcon, // receiver - edit receiving hours
  ShoppingCart as ShoppingCartIcon, // receiver - food preferences
  LocalShipping as LocalShippingIcon, // receiver - update capacity
  House as HouseIcon, // food donor - manage locations
} from '@mui/icons-material';
import { Link, generatePath } from 'react-router-dom';
import { BaseCard } from '../../../components/Common/BaseCard';
import { Colors } from '../../../assets/theme/Colors';
import rescuesIcon from '../../../assets/svg/rescuesIcon.svg';
import HelloUser from './Components/HelloUser';
import routes from '../../../routes';
import HtmlTooltip from '../../../components/Common/HtmlTooltip';
import { RubberStamp } from '../../Rescue/RescueMobileCard';
import useRescuerDashboardActions from '../../../hooks/useRescuerDashboardActions';
import {
  filterByDatePriority,
  getMyFrusMyEvents,
  getMyRescues,
  getRecentUnclosedRescues,
  getScheduleAvailableRescues,
  getTodayRescues,
  getUpcomingRescues,
  sortRescuesByDateAndPickupBegin,
} from '../../../helpers/RescuesHelper';
import useActiveUser from '../../../hooks/useActiveUser';
import DashboardMobileTabBadgeCounter from '../../../components/Common/DashboardMobileTabBadgeCounter';
import * as authServices from '../../../services/auth';
import { Roles } from '../../../models/roles';
import useActiveSite from '../../../hooks/useActiveSite';
import useHasActiveFeatureFlag from '../../../hooks/useFeatureFlags';
import { FF_EVENTS, FF_LEARNING_CENTER, FF_SITE_GOALS } from '../../../components/FeatureFlagWrapper';
import { fetchFoodDonorsWithPendingVerifications } from '../../../actions/foodDonors';
import { fetchSiteFoodDonorRescues, fetchSiteReceiverRescues, fetchSiteTodosIfNeeded } from '../../../actions/sites';
import NextRescues from './Components/NextRescues';
import MyImpactDashboardCard from './Components/MyImpactDashboardCard';
import MyBadgesDashboardCard from './Components/MyBadgesDashboardCard';
import { fetchSiteGoals } from '../../../actions/siteGoals';
import SiteGoalsDashboardCard from './Components/SiteGoalsDashboardCard';

const StyledBaseCard = (props) => (
  <BaseCard {...props} sx={{ height: '100%', borderRadius: '16px', minHeight: '40vw' }} />
);

const LandingCardText = styled(Typography)(({ theme }) => ({
  ...theme.typography.h5,
  fontWeight: 500,
  textDecoration: 'none',
  marginTop: theme.spacing(1),
  color: '#fff',
  flexGrow: 1,
  textAlign: 'center',
}));

const LandingBadge = styled(DashboardMobileTabBadgeCounter)(({ theme }) => ({
  '& .MuiBadge-badge': {
    fontSize: '1.2rem',
    top: 10,
    right: 10,
    padding: theme.spacing(1.4, 0.8),
  },
}));

const LandingCardWide = ({ text, color, icon, id = '', to = '#', disabled = false, badgeCount }) => (
  <StyledBaseCard data-introid={id}>
    <HtmlTooltip withBorder title={disabled ? 'This feature is disabled!' : ''}>
      <Box height={1} width={1}>
        {disabled && (
          <Box
            sx={{
              position: 'relative',
              transform: 'scale(0.7)',
              zIndex: 4,
              marginLeft: 25,
            }}
          >
            <RubberStamp isCancelled text="Coming soon" />
          </Box>
        )}
        <ButtonBase
          disabled={disabled}
          component={Link}
          to={to}
          focusRipple
          style={{
            filter: disabled ? 'grayscale(0.7) opacity(0.3)' : 'none',
            backgroundColor: color,
            width: '100%',
            height: '100%',
            borderRadius: 16,
          }}
        >
          <CardContent style={{ width: '100%' }}>
            <Box display="flex" flexDirection="column" justifyContent="space-between" alignItems="center">
              <LandingBadge style={{ fontSize: '2rem' }} badgeContent={badgeCount} color="primary">
                {icon}
              </LandingBadge>
              <LandingCardText>{text}</LandingCardText>
            </Box>
          </CardContent>
        </ButtonBase>
      </Box>
    </HtmlTooltip>
  </StyledBaseCard>
);

const avatarSx = {
  borderRadius: '12px',
  width: 70,
  height: 70,
};
const iconSx = {
  fontSize: '3rem',
};
const rescuesIconSx = {
  maxWidth: '100%',
  height: 'auto',
};

const LandingDashboard = () => {
  const dispatch = useDispatch();
  const activeUser = useActiveUser();
  const activeSite = useActiveSite();
  const eventsEntities = useSelector((state) => state.entities.events);
  const featureFlagsEntities = useSelector((state) => state.entities.featureFlags);
  const rescuesEntities = useSelector((state) => state.entities.rescues);
  const foodDonorsPendingRegistrationCount = useSelector((state) => state.entities.foodDonors.allIds).length;
  const sdTodosEntities = useSelector((state) => state.entities.sites.sdTodos);
  const environmentImpactEntities = useSelector((state) => state.entities.environmentImpact);
  const badgesEntities = useSelector((state) => state.entities.badges);
  const badgesList = get(badgesEntities, `byRescuerId[${activeUser.id}].badges`, []);
  const badgesLoading = get(badgesEntities, `byRescuerId[${activeUser.id}].inflight`, false);
  const siteGoalsEntities = useSelector((state) => state.entities.siteGoals);
  const siteGoals = Object.values(siteGoalsEntities.byId);
  const sdTodos = sdTodosEntities.bySiteId[activeSite.id];
  const hasEventsAccess = useHasActiveFeatureFlag(FF_EVENTS);
  const hasActiveSiteGoalsFeatureFlag = useHasActiveFeatureFlag(FF_SITE_GOALS);
  const hasActiveLearningCenterFeatureFlag = useHasActiveFeatureFlag(FF_LEARNING_CENTER);
  const { formatMessage } = useIntl();

  const userRoles = authServices.getUserRolesInCurrentlySelectedSite(activeUser);
  const sitesRescues = useSelector((state) => state.entities.sites.rescues);
  const sitesRescuesList = sitesRescues.bySiteId[activeSite.id] || [];

  // Receiver Dashboard
  const receiverRolesWithAccess = userRoles.filter(
    (role) => role.role_name === Roles.ReceiverLocationAdmin || role.role_name === Roles.ReceiverLocationStaff
  );
  const receiverId = receiverRolesWithAccess[0]?.receiver_id;
  const receiverRescues = sitesRescuesList.filter((rescue) => rescue.receiver_id === receiverId);

  // Food Donor Dashboard
  const foodDonorRolesWithAccess = userRoles.filter(
    (role) =>
      role.role_name === Roles.DonorAdmin ||
      role.role_name === Roles.DonorStaff ||
      role.role_name === Roles.DonorLocationAdmin ||
      role.role_name === Roles.DonorLocationStaff
  );
  const foodDonorId = foodDonorRolesWithAccess[0]?.donor_location_id;
  const foodDonorRescues = sitesRescuesList.filter((rescue) => rescue.location_id === foodDonorId);

  const SDToDoCount = Object.values(sdTodos || {}).reduce((sum, todos) => sum + (todos?.length || 0), 0);

  const myRescuesCount =
    getMyRescues(activeUser.id, rescuesEntities).length +
    getRecentUnclosedRescues(activeUser.id, rescuesEntities).length;
  const eventsCount = getMyFrusMyEvents(eventsEntities, activeUser.id).events.length;
  const claimRescueCount = getScheduleAvailableRescues(activeUser.id, rescuesEntities).data.length;

  // role check
  const isSiteDirector = authServices.hasAnyRoleInSite(activeUser, activeSite.id, [
    Roles.SiteDirector,
    Roles.SiteCoordinator,
    Roles.NationalSiteDirector,
    Roles.Admin,
  ]);
  const isRescuer = authServices.hasRoleInAnySite(activeUser, Roles.Rescuer);
  const isDonor = authServices.hasAnyRoleInSite(activeUser, activeSite.id, [
    Roles.DonorAdmin,
    Roles.DonorStaff,
    Roles.DonorLocationAdmin,
    Roles.DonorLocationStaff,
  ]);
  const isReceiver = authServices.hasAnyRoleInCurrentlySelectedSite(activeUser, [
    Roles.ReceiverLocationAdmin,
    Roles.ReceiverLocationStaff,
    Roles.ReceiverAdmin,
    Roles.ReceiverStaff,
  ]);
  const hasAccessToMultipleSitesAsSD = (authServices.getUserSitesByRoles(
    activeUser.role_assignments,
    [
      Roles.SiteDirector,
      Roles.SiteCoordinator,
    ]
  ) || []).length > 1;

  const isRescuerInCurrentlySelectedSite = authServices.hasRoleInCurrentlySelectedSite(activeUser, Roles.Rescuer);

  const { fetchMyEvents, fetchMyFrusTab, fetchBadgesTab, fetchEnviromentImpact, fetchMyBadges } =
    useRescuerDashboardActions();

  useEffect(() => {
    if (isRescuer) {
      fetchMyEvents(false, false);
      fetchMyFrusTab(false, false);

      fetchBadgesTab(false, true);

      fetchEnviromentImpact();
      fetchMyBadges();
      dispatch(fetchSiteGoals({ sites_ids: activeSite.id }));
    }

    if (isSiteDirector) {
      dispatch(fetchSiteTodosIfNeeded(activeSite.id));
      dispatch(fetchFoodDonorsWithPendingVerifications([activeSite.id]));
    }
  }, [dispatch, fetchMyEvents, fetchMyFrusTab, isRescuer, isSiteDirector, hasActiveSiteGoalsFeatureFlag]);

  useEffect(() => {
    if (isReceiver && receiverId) {
      dispatch(fetchSiteReceiverRescues(activeSite.id, receiverId));
    }
  }, [dispatch, isReceiver, receiverId, activeSite]);

  useEffect(() => {
    if (isDonor && foodDonorId) {
      dispatch(fetchSiteFoodDonorRescues(activeSite.id, foodDonorId));
    }
  }, [dispatch, isDonor, foodDonorId, activeSite]);

  // Next rescues rescuer
  const todayRescuerRescues = getTodayRescues(activeUser.id, rescuesEntities);
  const upcomingRescuerRescues = getUpcomingRescues(activeUser.id, rescuesEntities);
  const nextRescuerRescues = filterByDatePriority([...todayRescuerRescues, ...upcomingRescuerRescues]);

  // Next rescues receiver
  const todayReceiverRescues = receiverRescues
    .filter((rescue) => rescue.date === moment().format('YYYYMMDD'))
    .sort(sortRescuesByDateAndPickupBegin);
  const upcomingReceiverRescues = receiverRescues
    .filter((rescue) => rescue.date !== moment().format('YYYYMMDD'))
    .sort(sortRescuesByDateAndPickupBegin);
  const nextReceiverRescues = filterByDatePriority([...todayReceiverRescues, ...upcomingReceiverRescues]);

  // Next rescues food donor
  const todayFoodDonorRescues = foodDonorRescues
    .filter((rescue) => rescue.date === moment().format('YYYYMMDD'))
    .sort(sortRescuesByDateAndPickupBegin);
  const upcomingFoodDonorRescues = foodDonorRescues
    .filter((rescue) => rescue.date !== moment().format('YYYYMMDD'))
    .sort(sortRescuesByDateAndPickupBegin);
  const nextFoodDonorRescues = filterByDatePriority([...todayFoodDonorRescues, ...upcomingFoodDonorRescues]);

  const renderNextRescueSummary = (todayRescues, upcomingRescues, nextRescues) => {
    // Today
    if (todayRescues.length > 0) {
      // More than one rescue today
      if (todayRescues.length > 1) {
        return `${formatMessage({ id: 'dashboard-mobile.next-rescues-you-have' })} ${
          todayRescues.length
        } ${formatMessage({ id: 'dashboard-mobile.next-rescues-today' })}`;
      }

      return formatMessage({ id: 'dashboard-mobile.next-rescue-today' });
    }

    if (upcomingRescues.length > 0) {
      // Tomorrow
      const tomorrow = moment().add(1, 'day').format('YYYYMMDD');
      const tomorrowRescue = upcomingRescues.find((rescue) => rescue.date === tomorrow);

      if (tomorrowRescue) {
        // More than one rescue tomorrow
        if (nextRescues.length > 1) {
          return `${formatMessage({ id: 'dashboard-mobile.next-rescues-you-have' })} ${
            nextRescues.length
          } ${formatMessage({ id: 'dashboard-mobile.next-rescues-tomorrow' })}`;
        }

        return formatMessage({ id: 'dashboard-mobile.next-rescue-tomorrow' });
      }

      // In few days
      const nextRescue = upcomingRescues[0];
      const today = moment().startOf('day');
      const dayDiff = moment(nextRescue?.date, 'YYYYMMDD').startOf('day').diff(today, 'days');

      return `${formatMessage({ id: 'dashboard-mobile.next-rescue-in' })} ${dayDiff} ${formatMessage({
        id: 'dashboard-mobile.next-rescue-days',
      })}`;
    }

    return formatMessage({ id: 'dashboard-mobile.next-rescue-no-upcoming' });
  };

  // wait for feature flags fetch before showing user page
  if (featureFlagsEntities.inflight) {
    return null;
  }

  return (
    <Box sx={{ padding: 2, width: '100%' }} data-testid="tiled-dashboard">
      <HelloUser hideSubtitle={isRescuer || isReceiver} />
      {/* Anchor for Ask SD tour step */}
      <div id="ask-sd" style={{ position: 'fixed', top: '50%', left: '50%' }} />

      <Grid container direction="row" spacing={3}>
        {isRescuer && (
          <>
            <Grid item xs={12}>
              <NextRescues
                id="next-rescue"
                isLoading={rescuesEntities.claimedAndAdoptedIds?.[activeUser.id]?.inflight}
                nextRescues={nextRescuerRescues}
                renderNextRescueSummary={() =>
                  renderNextRescueSummary(todayRescuerRescues, upcomingRescuerRescues, nextRescuerRescues)
                }
              />
            </Grid>
            <Grid item xs={12}>
              <MyImpactDashboardCard
                id="my-impact"
                environmentImpact={environmentImpactEntities.all}
                isLoading={environmentImpactEntities.inflight}
              />
            </Grid>
          </>
        )}
        {isSiteDirector && (
          <>
            <Grid item xs={6}>
              <LandingCardWide
                id="site"
                to={generatePath(routes.mobileDashboardSchedule)}
                text={formatMessage({ id: 'dashboard-mobile.tiles.site' })}
                color={Colors.schedule.color}
                icon={
                  <Avatar sx={[avatarSx, { backgroundColor: Colors.schedule.color }]}>
                    <StoreMallDirectoryIcon sx={iconSx} />
                  </Avatar>
                }
              />
            </Grid>

            <Grid item xs={6}>
              <LandingCardWide
                id="to-do"
                badgeCount={SDToDoCount}
                to={generatePath(routes.mobileToDo)}
                text={formatMessage({ id: 'dashboard-mobile.tiles.sd-to-do' })}
                color={Colors.pendingFoodDonor.color}
                icon={
                  <Avatar sx={[avatarSx, { backgroundColor: Colors.pendingFoodDonor.color }]}>
                    <QueryBuilderIcon sx={iconSx} />
                  </Avatar>
                }
              />
            </Grid>

            {hasAccessToMultipleSitesAsSD && (
              <Grid item xs={6}>
                <LandingCardWide
                  badgeCount={foodDonorsPendingRegistrationCount}
                  to={generatePath(routes.sites)}
                  text={formatMessage({ id: 'dashboard-mobile.tiles.sites' })}
                  color={Colors.switchSites.color}
                  icon={
                    <Avatar sx={[avatarSx, { backgroundColor: Colors.switchSites.color }]}>
                      <StoreMallDirectoryIcon sx={iconSx} />
                    </Avatar>
                  }
                />
              </Grid>
            )}
          </>
        )}

        {isRescuer && (
          <>
            <Grid item xs={6}>
              <LandingCardWide
                id="claim-rescue"
                to={generatePath(routes.mobileDashboardClaimRescue)}
                text={formatMessage({ id: 'dashboard-mobile.tiles.claim-rescue' })}
                color={Colors.rescues.claim.color}
                badgeCount={claimRescueCount}
                icon={
                  <Avatar sx={[avatarSx, { backgroundColor: Colors.rescues.claim.color }]}>
                    <AddShoppingCartIcon sx={iconSx} />
                  </Avatar>
                }
              />
            </Grid>
            <Grid item xs={6}>
              <LandingCardWide
                id="my-rescues"
                to={generatePath(routes.mobileDashboardMyRescues)}
                text={formatMessage({ id: 'dashboard-mobile.tiles.my-rescues' })}
                color={Colors.rescues.today.color}
                badgeCount={myRescuesCount}
                icon={
                  <Avatar sx={[avatarSx, { backgroundColor: Colors.rescues.today.color }]}>
                    <img src={rescuesIcon} style={rescuesIconSx} alt="My Rescues" />
                  </Avatar>
                }
              />
            </Grid>
          </>
        )}

        {hasEventsAccess && (isSiteDirector || isRescuerInCurrentlySelectedSite) && (
          <Grid item xs={6}>
            <LandingCardWide
              id="events"
              to={generatePath(routes.mobileDashboardEvents, { tab: 'my-events' })}
              text={formatMessage({ id: 'dashboard-mobile.tiles.events' })}
              color={Colors.event.color}
              badgeCount={eventsCount}
              icon={
                <Avatar sx={[avatarSx, { backgroundColor: Colors.event.color }]}>
                  <EmojiPeopleIcon sx={iconSx} />
                </Avatar>
              }
            />
          </Grid>
        )}

        {isRescuerInCurrentlySelectedSite && (
          <Grid item xs={6}>
            <LandingCardWide
              id="id-mode"
              to={generatePath(routes.idMode)}
              text={formatMessage({ id: 'dashboard-mobile.tiles.id-mode' })}
              color={Colors.idMode.color}
              icon={
                <Avatar sx={[avatarSx, { backgroundColor: Colors.idMode.color }]}>
                  <PortraitIcon sx={iconSx} />
                </Avatar>
              }
            />
          </Grid>
        )}

        {hasActiveLearningCenterFeatureFlag && (isSiteDirector || isRescuer) && (
          <Grid item xs={6}>
            <LandingCardWide
              id="learn"
              to={generatePath(routes.mobileDashboardLearn)}
              text={formatMessage({ id: 'dashboard-mobile.tiles.learn' })}
              color={Colors.learn.color}
              icon={
                <Avatar sx={[avatarSx, { backgroundColor: Colors.learn.color }]}>
                  <SchoolIcon sx={iconSx} />
                </Avatar>
              }
            />
          </Grid>
        )}

        {isRescuer && (
          <>
            <Grid item xs={12}>
              <MyBadgesDashboardCard badgesList={badgesList} isLoading={badgesLoading} id="badges" />
            </Grid>
            <Grid item xs={12}>
              <SiteGoalsDashboardCard siteGoals={siteGoals} isLoading={siteGoalsEntities.inflight} id="site-goals" />
            </Grid>
          </>
        )}

        {isReceiver && (
          <>
            {/* Check if the user has more roles and if so display an agency name */}
            {(isRescuer || isDonor) && (
              <Grid item xs={12}>
                <Box mb={2}>
                  <Divider />
                </Box>
                <Typography variant="h6">
                  {receiverRolesWithAccess[0]?.receiver_name ? `${receiverRolesWithAccess[0]?.receiver_name}` : 'Receiving Agency'}
                </Typography>
              </Grid>
            )}

            {/* Next Rescues */}
            <Grid item xs={12}>
              <NextRescues
                id="receiver-next-rescue"
                isLoading={sitesRescues.inflight}
                nextRescues={filterByDatePriority(receiverRescues).sort(sortRescuesByDateAndPickupBegin)}
                renderNextRescueSummary={() =>
                  renderNextRescueSummary(todayReceiverRescues, upcomingReceiverRescues, nextReceiverRescues)
                }
                isReceiverView
              />
            </Grid>

            {/* Schedule */}
            <Grid item xs={6}>
              <LandingCardWide
                id="receiver-schedule"
                to={generatePath(routes.mobileDashboardReceiverSchedule)}
                text={formatMessage({ id: 'dashboard-mobile.tiles.receiver-view-schedule' })}
                color={Colors.receiverDashboard.viewSchedule.color}
                icon={
                  <Avatar sx={[avatarSx, { backgroundColor: Colors.receiverDashboard.viewSchedule.color }]}>
                    <CalendarTodayIcon sx={iconSx} />
                  </Avatar>
                }
              />
            </Grid>

            {/* Add Staff */}
            <Grid item xs={6}>
              <LandingCardWide
                id="receiver-add-staff"
                to={generatePath(routes.receiverStaffAdd, { receiverId })}
                text={formatMessage({ id: 'dashboard-mobile.tiles.receiver-view-add-staff' })}
                color={Colors.receiverDashboard.addStaff.color}
                icon={
                  <Avatar sx={[avatarSx, { backgroundColor: Colors.receiverDashboard.addStaff.color }]}>
                    <PersonAddIcon sx={iconSx} />
                  </Avatar>
                }
              />
            </Grid>

            {/* Edit Receiving Hours */}
            <Grid item xs={6}>
              <LandingCardWide
                id="receiving-hours"
                to={generatePath(routes.mobileDashboardReceiverEditReceivingHours)}
                text={formatMessage({ id: 'dashboard-mobile.tiles.receiver-view-edit-receiving-hours' })}
                color={Colors.receiverDashboard.editReceivingHours.color}
                icon={
                  <Avatar sx={[avatarSx, { backgroundColor: Colors.receiverDashboard.editReceivingHours.color }]}>
                    <AccessTimeIcon sx={iconSx} />
                  </Avatar>
                }
              />
            </Grid>

            {/* Food Preferences */}
            <Grid item xs={6}>
              <LandingCardWide
                id="receiver-food-preferences"
                to={generatePath(routes.mobileDashboardReceiverFoodPreferences)}
                text={formatMessage({ id: 'dashboard-mobile.tiles.receiver-view-adjust-food-preferences' })}
                color={Colors.receiverDashboard.foodPreferences.color}
                icon={
                  <Avatar sx={[avatarSx, { backgroundColor: Colors.receiverDashboard.foodPreferences.color }]}>
                    <ShoppingCartIcon sx={iconSx} />
                  </Avatar>
                }
              />
            </Grid>

            {/* Update Capacity */}
            <Grid item xs={6}>
              <LandingCardWide
                id="receiver-capacity"
                to={generatePath(routes.mobileDashboardReceiverCapacity)}
                text={formatMessage({ id: 'dashboard-mobile.tiles.receiver-view-update-capacity' })}
                color={Colors.receiverDashboard.updateCapacity.color}
                icon={
                  <Avatar sx={[avatarSx, { backgroundColor: Colors.receiverDashboard.updateCapacity.color }]}>
                    <LocalShippingIcon sx={iconSx} />
                  </Avatar>
                }
              />
            </Grid>
          </>
        )}

        {isDonor && (
          <>
            {/* Check if the user has more roles and if so display food donor name */}
            {(isRescuer || isReceiver) && (
              <Grid item xs={12}>
                <Box mb={2}>
                  <Divider />
                </Box>
                <Typography variant="h6">
                  {foodDonorRolesWithAccess[0]?.donor_location_name ? `${foodDonorRolesWithAccess[0]?.donor_location_name}` : 'Receiving Agency'}
                </Typography>
              </Grid>
            )}

            {/* Next Food Donor Rescues */}
            <Grid item xs={12}>
              <NextRescues
                id="food-donor-next-rescue"
                isLoading={sitesRescues.inflight}
                nextRescues={filterByDatePriority(foodDonorRescues).sort(sortRescuesByDateAndPickupBegin)}
                renderNextRescueSummary={() =>
                  renderNextRescueSummary(todayFoodDonorRescues, upcomingFoodDonorRescues, nextFoodDonorRescues)
                }
                isDonorView
              />
            </Grid>

            {/* Donate Food */}
            <Grid item xs={6}>
              <LandingCardWide
                to={generatePath(routes.mobileDashboardFoodDonorRequests)}
                text={formatMessage({ id: 'dashboard-mobile.tiles.food-donor-donate-food' })}
                color={Colors.foodDonorDashboard.donateFood.color}
                icon={
                  <Avatar sx={[avatarSx, { backgroundColor: Colors.foodDonorDashboard.donateFood.color }]}>
                    <ShoppingCartIcon sx={iconSx} />
                  </Avatar>
                }
              />
            </Grid>

            {/* Food Donor Schedule */}
            <Grid item xs={6}>
              <LandingCardWide
                id="food-donor-schedule"
                to={generatePath(routes.mobileDashboardFoodDonorSchedule)}
                text={formatMessage({ id: 'dashboard-mobile.tiles.food-donor-view-schedule' })}
                color={Colors.foodDonorDashboard.viewSchedule.color}
                icon={
                  <Avatar sx={[avatarSx, { backgroundColor: Colors.foodDonorDashboard.viewSchedule.color }]}>
                    <CalendarTodayIcon sx={iconSx} />
                  </Avatar>
                }
              />
            </Grid>

            {/* Food Donor Locations */}
            <Grid item xs={6}>
              <LandingCardWide
                id="food-donor-locations"
                to={generatePath(routes.mobileDashboardFoodDonorLocations)}
                text={formatMessage({ id: 'dashboard-mobile.tiles.food-donor-manage-locations' })}
                color={Colors.foodDonorDashboard.locations.color}
                icon={
                  <Avatar sx={[avatarSx, { backgroundColor: Colors.foodDonorDashboard.locations.color }]}>
                    <HouseIcon sx={iconSx} />
                  </Avatar>
                }
              />
            </Grid>

            {/* Food Donor Add Staff */}
            <Grid item xs={6}>
              <LandingCardWide
                id="food-donor-add-staff"
                to={generatePath(routes.mobileDashboardFoodDonorAddStaff)}
                text={formatMessage({ id: 'dashboard-mobile.tiles.receiver-view-add-staff' })}
                color={Colors.receiverDashboard.addStaff.color}
                icon={
                  <Avatar sx={[avatarSx, { backgroundColor: Colors.receiverDashboard.addStaff.color }]}>
                    <PersonAddIcon sx={iconSx} />
                  </Avatar>
                }
              />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default LandingDashboard;
